import * as Dialog from '@radix-ui/react-dialog'
import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useT } from '../i18n'

/**
 * Die Suche als Fenster statt als Feld in der Kopfzeile.
 *
 * # Warum das Feld weg ist
 *
 * Ein Suchfeld mitten in der Kopfzeile kostet ein Drittel ihrer Breite — und
 * zwar dauerhaft, für eine Handlung, die vielleicht jeder zwanzigste Besuch
 * braucht. Dafür wich der Ladenname, die Verweise rückten zusammen, und am
 * Telefon musste das Feld ganz verschwinden, weil die Kopfzeile sonst vier
 * Zeilen hoch wurde.
 *
 * Ein Knopf kostet ein Quadrat. Was er öffnet, hat dann den ganzen Platz:
 * ein großes Feld, in dem man den Suchbegriff auch lesen kann.
 *
 * # Warum es dasselbe Fenster auf allen Größen ist
 *
 * Vorher gab es zwei Wege — das Feld auf dem Schreibtisch, einen Verweis auf
 * eine Suchseite am Telefon. Zwei Wege sind zwei Verhaltensweisen, und die
 * zweite wird beim Ändern vergessen. Jetzt einer.
 */
export function Suchfenster() {
  const t = useT()
  const navigate = useNavigate()
  const [offen, setOffen] = useState(false)
  const [begriff, setBegriff] = useState('')

  function suchen(ereignis: FormEvent) {
    ereignis.preventDefault()
    const wert = begriff.trim()
    setOffen(false)
    // Auch leer: Dann steht das ganze Sortiment da, und das ist eine
    // brauchbare Antwort auf „zeig mir was".
    navigate(wert ? `/suche?q=${encodeURIComponent(wert)}` : '/suche')
  }

  return (
    <Dialog.Root open={offen} onOpenChange={setOffen}>
      <Dialog.Trigger asChild>
        <button className="sf-symbolknopf" type="button" aria-label={t('suche.feldLabel')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.7" />
            <path d="m16.5 16.5 4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </button>
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className="hinterlegung" />
        <Dialog.Content className="suchfenster" aria-describedby={undefined}>
          <Dialog.Title className="nur-fuer-screenreader">{t('suche.feldLabel')}</Dialog.Title>

          <form className="suchfenster-form" role="search" onSubmit={suchen}>
            <svg
              className="suchfenster-zeichen"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.7" />
              <path d="m16.5 16.5 4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            </svg>

            <label className="nur-fuer-screenreader" htmlFor="suchfeld">
              {t('suche.feldLabel')}
            </label>
            <input
              id="suchfeld"
              className="suchfenster-feld"
              type="search"
              name="q"
              /* Der Fokus liegt beim Öffnen hier: Wer die Suche aufmacht, will
                 tippen — ein Fenster, in dem man erst klicken muss, ist ein
                 Zwischenschritt ohne Zweck. Radix legt ihn auf das erste
                 fokussierbare Element, und das ist dieses Feld. */
              autoFocus
              placeholder={t('suche.platzhalter')}
              value={begriff}
              onChange={(e) => setBegriff(e.target.value)}
            />
            <button className="knopf" type="submit">
              {t('suche.knopf')}
            </button>
          </form>

          <p className="suchfenster-hinweis">{t('suche.fensterHinweis')}</p>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
